import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/calendar.css';
import DeleteButton from '../components/DeleteButton';
import { API_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const typeLabels = {
  date: { label: 'Date Night', color: '#3b82f6', icon: '💙' },
  anniversary: { label: 'Anniversary', color: '#f59e42', icon: '🎉' },
  special: { label: 'Special', color: '#a21caf', icon: '⭐' }
};

const Calendar = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    description: '',
    type: 'date'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events`);
      setEvents(response.data);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch events');
      setLoading(false);
    }
  };

  // Restrict access for unpartnered or soft-deleted users
  if (!user?.partner || user?.deletionRequestedAt) {
    return <div style={{textAlign: 'center', marginTop: 40, color: '#888'}}>You must be in a couple to view the calendar.</div>;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewEvent(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/events`, newEvent);
      setNewEvent({
        title: '',
        date: '',
        description: '',
        type: 'date'
      });
      setShowModal(false);
      fetchEvents();
    } catch (error) {
      setError('Failed to add event');
    }
  };

  const handleEventDeleted = (deletedId) => {
    setEvents(prev => prev.filter(event => event._id !== deletedId));
  };

  if (loading) {
    return <div>Loading calendar...</div>;
  }

  return (
    <div className="calendar-container">
      <h2 className="calendar-title">Our Calendar</h2>
      {error && <div className="error-message">{error}</div>}

      <div className="events-list card-holder">
        {events.length === 0 && (
          <div style={{textAlign: 'center', color: '#bbb', fontSize: '1.1rem', marginTop: 24}}>No events yet. Add your first one!</div>
        )}
        {events.map(event => {
          const typeInfo = typeLabels[event.type] || typeLabels.date;
          return (
            <div key={event._id} className={`event-card ${event.type}`} style={{borderLeft: `8px solid ${typeInfo.color}`}}>
              <div className="event-header" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                  <span style={{fontSize: '1.6rem'}}>{typeInfo.icon}</span>
                  <span className="event-title">{event.title}</span>
                  <span className="event-type-badge" style={{background: typeInfo.color}}>{typeInfo.label}</span>
                </div>
                <DeleteButton 
                  itemId={event._id} 
                  itemType="event" 
                  onSuccess={handleEventDeleted}
                  title="Delete Event"
                  message="Are you sure you want to delete this event? This action cannot be undone."
                  size="small"
                />
              </div>
              <div className="event-date">
                {new Date(event.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div className="event-desc">{event.description}</div>
            </div>
          );
        })}
      </div>

      {/* Floating Action Button */}
      <button className="fab-add-event" onClick={() => setShowModal(true)} title="Add Event">
        +
      </button>

      {/* Modal Popup for Event Form */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            <h3 className="modal-title">Add New Event</h3>
            <form onSubmit={handleSubmit} className="calendar-form" autoComplete="off">
              <div className="form-group">
                <input
                  type="text"
                  name="title"
                  value={newEvent.title}
                  onChange={handleChange}
                  placeholder=" "
                  required
                />
                <label>Event Title</label>
              </div>
              <div className="form-group">
                <input
                  type="date"
                  name="date"
                  value={newEvent.date}
                  onChange={handleChange}
                  placeholder=" "
                  required
                />
                <label>Date</label>
              </div>
              <div className="form-group">
                <select
                  name="type"
                  value={newEvent.type}
                  onChange={handleChange}
                  required
                  className="event-type-select"
                >
                  <option value="date">Date Night</option>
                  <option value="anniversary">Anniversary</option>
                  <option value="special">Special Occasion</option>
                </select>
              </div>
              <div className="form-group">
                <textarea
                  name="description"
                  value={newEvent.description}
                  onChange={handleChange}
                  placeholder=" "
                  rows="4"
                />
                <label>Event Description</label>
              </div>
              <button type="submit" className="add-event-btn">Add Event</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar; 